'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Button } from '@virginmediao2/momentum-nextjs/button';
import {
  Card,
  CardFooter,
  CardHeader,
} from '@virginmediao2/momentum-nextjs/card';
import { Divider } from '@virginmediao2/momentum-nextjs/divider';
import { Flex } from '@virginmediao2/momentum-nextjs/flex';
import {
  ErrorAlert,
  ErrorAlertsRef,
  FormInput,
  FormRadioGroup,
  FormRadioInput,
  FormSelect,
  FormTextarea,
} from '@virginmediao2/momentum-nextjs/forms';
import { Icon, IconProps } from '@virginmediao2/momentum-nextjs/icon';
import { Text } from '@virginmediao2/momentum-nextjs/text';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';

import {
  FalBriefcase,
  FalCalendarDays,
  FalCreditCard,
} from '@virginmediao2/momentum-icons/icons/fal';

import { sendComplaintsFormRequest } from '@/lib/complaints-form';
import { getSubmisionURL } from '@/lib/complaints-form/send-request';
import { ukMobileNumberRegex } from '@/lib/utils/regex-validators';

import styles from './complaints-form.module.css';
import { complaintsList, complaintsListMap } from './complaintsList';

enum optionNames {
  payMonthly = 'payMonthly',
  business = 'business',
  payAsYouGo = 'payAsYouGo',
}

const formOptionInputList: Array<{
  name: optionNames;
  label: string;
  icon: IconProps['icon'];
}> = [
  {
    name: optionNames.payMonthly,
    label: 'Pay Monthly',
    icon: FalCalendarDays,
  },
  {
    name: optionNames.business,
    label: 'Business',
    icon: FalBriefcase,
  },
  {
    name: optionNames.payAsYouGo,
    label: 'Pay as You Go',
    icon: FalCreditCard,
  },
];

const Options = ({
  optionsData,
}: {
  optionsData: Record<string, string>[];
}) => (
  <>
    <option value='' disabled>
      Select
    </option>
    {optionsData.map(({ label, value }) => (
      <option key={label} value={value}>
        {label}
      </option>
    ))}
  </>
);

export const ComplaintsForm = ({
  errorMessage,
  recaptchaSiteKey,
}: {
  errorMessage: JSX.Element;
  recaptchaSiteKey: string;
}) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const errorAlertsRef = useRef<ErrorAlertsRef>(null);

  const [apiError, setApiError] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  const [service, setService] = useState('');
  const [issueTypeOptions, setIssueTypeOptions] = useState<
    keyof typeof complaintsListMap | ''
  >('');

  const router = useRouter();

  const complaintsFormSchema = z
    .object({
      isCustomer: z.string({
        invalid_type_error: 'Please specify if you are a customer',
      }),
      mobileNumber: z.string().optional(),
      complaintRadio: z.any(),
      issueDescription: z.string().min(1, 'Select an issue description'),
      issueType: z.string().optional(),
      firstName: z.string().min(1, 'First name is required'),
      lastName: z.string().min(1, 'Last name is required'),
      addressLine1: z.string().min(1, 'Address line 1 is required'),
      addressLine2: z.string().optional(),
      town: z.string().min(1, 'Town is required'),
      country: z.string().min(1, 'Country is required'),
      postCode: z.string().min(1, 'Postcode is required'),
      issueDetails: z
        .string()
        .min(50, 'The details requires at least 10 words'),
      howToFixTheIssue: z.string().min(1, 'How to fix the issue is required'),
      contactMobileNumber: z
        .string()
        .max(11)
        .regex(ukMobileNumberRegex, 'Invalid mobile number'),
      email: z
        .string()
        .min(1, 'Email is required')
        .refine((val) => !val || z.string().email().safeParse(val).success, {
          message: 'Invalid email address',
        }),
      confirmEmail: z
        .string()
        .min(1, 'Confirm email is required')
        .refine((val) => !val || z.string().email().safeParse(val).success, {
          message: 'Invalid email address',
        }),
      preferContact: z.string(),
    })
    .superRefine(({ isCustomer, complaintRadio }, ctx) => {
      if (isCustomer === 'yes' && (!complaintRadio || complaintRadio === '')) {
        ctx.addIssue({
          code: 'custom',
          path: ['complaintRadio'],
          message: 'Please select a service related to your complaint',
        });
      }
    })
    .superRefine(({ issueDescription, issueType }, ctx) => {
      if (issueDescription && !issueType) {
        ctx.addIssue({
          code: 'custom',
          path: ['issueType'],
          message: 'Please select a type of problem',
        });
      }
    })
    .superRefine(({ isCustomer, mobileNumber }, ctx) => {
      if (isCustomer === 'yes') {
        if (!mobileNumber || mobileNumber.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['mobileNumber'],
            message: 'Please enter your O2 mobile number',
          });
        } else if (!ukMobileNumberRegex.test(mobileNumber)) {
          ctx.addIssue({
            code: 'custom',
            path: ['mobileNumber'],
            message: 'Invalid mobile number',
          });
        }
      }
    })
    .superRefine(({ email, confirmEmail }, ctx) => {
      if (email && confirmEmail && email !== confirmEmail) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmEmail'],
          message: 'Email addresses do not match',
        });
      }
    });

  type ComplaintsFormData = z.infer<typeof complaintsFormSchema>;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.recaptchaOptions = { enterprise: true };
    }
  }, []);

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isValid },
  } = useForm<ComplaintsFormData>({
    resolver: zodResolver(complaintsFormSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: ComplaintsFormData) => {
    try {
      if (!isValid) return;

      let recaptchaToken = '';
      try {
        recaptchaRef.current?.reset();
        recaptchaToken = (await recaptchaRef.current?.executeAsync()) || '';
      } catch (err) {
        return;
      }

      const SUBMISSION_SUCCESS_URL = await getSubmisionURL();
      setApiError(false);

      const userAgent =
        typeof window !== 'undefined' ? window.navigator.userAgent : '';

      await sendComplaintsFormRequest({
        ...data,
        recaptchaToken,
        userAgent,
        service,
      });
      reset();
      router.push(SUBMISSION_SUCCESS_URL);
    } catch (error) {
      setApiError(true);
      console.error('Submission error:', error);
    }
  };

  return (
    <>
      {apiError && (
        <Box margin='lg 0' border={false}>
          {errorMessage}
        </Box>
      )}
      {!isValid && errors && (
        <Box border={false}>
          <ErrorAlert
            ref={errorAlertsRef}
            errors={errors}
            setFocus={(name: string) => setFocus(name as any)}
          />
        </Box>
      )}
      <Flex direction='column' className={styles['complaints-form']}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Box direction='row' directionSm='column' justify='between'>
              <FormSelect
                id='isCustomer'
                label='Are you a customer?'
                {...register('isCustomer')}
                error={errors?.isCustomer}
                onChange={(e) => {
                  setIsCustomer(e.target.value === 'yes');
                  setService('');
                }}
                marginBottom
                defaultValue=''
              >
                <Options
                  optionsData={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </FormSelect>

              {isCustomer && (
                <FormInput
                  id='mobileNumber'
                  label='Please enter your O2 mobile number'
                  {...register('mobileNumber')}
                  error={errors?.mobileNumber}
                  placeholder='07917112233'
                  maxLength={11}
                />
              )}
            </Box>
            <Box margin={'0 0 lg 0'}>
              <Text size='T400' weight='bold'>
                Your complaint
              </Text>
              <FormRadioGroup
                id='complaintRadio'
                label='Which of the following services does your complaint relate to?'
                error={errors?.complaintRadio?.message as string}
              >
                <Box
                  direction='row'
                  directionSm='column'
                  justify='center'
                  align='center'
                >
                  {formOptionInputList.map((item) => (
                    <Card
                      bg={isCustomer ? 'primary' : 'secondary'}
                      key={item.name}
                      border
                      padding={'lg'}
                      margin={'sm'}
                      rounded={'sm'}
                      align='center'
                      width={'full-width'}
                      aria-label={item.label}
                    >
                      <CardHeader align='center' id={item.name}>
                        <Icon icon={item.icon} color='dark' size='lg' />
                      </CardHeader>
                      <CardFooter>
                        <FormRadioInput
                          label={item.label}
                          isFlag={false}
                          isBordered={false}
                          disabled={!isCustomer}
                          value='no'
                          {...register('complaintRadio')}
                          onChange={() => setService(item.name)}
                          checked={service === item.name}
                          data-testid={`complaint-radio-${item.name}`}
                        />
                      </CardFooter>
                    </Card>
                  ))}
                </Box>
              </FormRadioGroup>
            </Box>
            <FormSelect
              id='issueDescription'
              label={'What issue best describes your problem?'}
              {...register('issueDescription')}
              error={errors?.issueDescription}
              marginBottom
              onChange={(e) => {
                setIssueTypeOptions(
                  e.target.value as keyof typeof complaintsListMap
                );
              }}
              defaultValue=''
            >
              <Options optionsData={complaintsList} />
            </FormSelect>
            <FormSelect
              id='issueType'
              disabled={!issueTypeOptions}
              label={'What type of problem do you have?'}
              {...register('issueType')}
              error={errors?.issueType}
              marginBottom
              defaultValue=''
            >
              <Options
                optionsData={
                  issueTypeOptions ? complaintsListMap[issueTypeOptions] : []
                }
              />
            </FormSelect>
          </Box>
          <Divider color='light' margin={'md 0 lg 0'} />
          <Box margin={'lg 0 lg'}>
            <Text weight='bold' size='T500' marginBottom>
              Your details
            </Text>
            <FormInput
              id='firstName'
              label={'First name'}
              {...register('firstName')}
              error={errors?.firstName}
              marginBottom
            />

            <FormInput
              id='lastName'
              label={'Last name'}
              {...register('lastName')}
              error={errors?.lastName}
              marginBottom
            />

            <div className={styles['field-row']}>
              <div className={styles['input-row']}>
                <FormInput
                  id='addressLine1'
                  label='Address line 1'
                  {...register('addressLine1')}
                  error={errors?.addressLine1}
                />
              </div>
              <div className={styles['input-row']}>
                <FormInput
                  id='addressLine2'
                  label='Address line 2'
                  {...register('addressLine2')}
                  error={errors?.addressLine2}
                />
              </div>
            </div>

            <div className={styles['field-row']}>
              <div className={styles['input-row']}>
                <FormInput
                  id='town'
                  label={'Town'}
                  {...register('town')}
                  error={errors?.town}
                />
              </div>
              <div className={styles['input-row']}>
                <FormInput
                  id='country'
                  label={'Country'}
                  {...register('country')}
                  error={errors?.country}
                />
              </div>
            </div>

            <FormInput
              id='postCode'
              label={'Post Code'}
              {...register('postCode')}
              error={errors?.postCode}
            />
          </Box>

          <FormTextarea
            id='issueDetails'
            dataTestId='issueDetails'
            label={<Text>Please provide details of your issue</Text>}
            {...register('issueDetails')}
            placeholder='Type here'
            error={errors?.issueDetails}
            marginBottom
          />

          <FormTextarea
            id='howToFixTheIssue'
            label={<Text>What would you like O2 to do about your issue?</Text>}
            dataTestId='howToFixTheIssue'
            {...register('howToFixTheIssue')}
            placeholder='Type here'
            error={errors?.howToFixTheIssue}
            marginBottom
          />
          <Box>
            <FormInput
              id='contactMobileNumber'
              label={'Contact telephone number'}
              {...register('contactMobileNumber')}
              error={errors?.contactMobileNumber}
              marginBottom
              maxLength={11}
            />
          </Box>
          <Box>
            <div className={styles['field-row']}>
              <div className={styles['input-row']}>
                <FormInput
                  id='email'
                  label={'Email address'}
                  {...register('email')}
                  error={errors?.email}
                  marginBottom
                />
              </div>
              <div className={styles['input-row']}>
                <FormInput
                  id='confirmEmail'
                  label={'Confirm email address'}
                  {...register('confirmEmail')}
                  error={errors?.confirmEmail}
                  marginBottom
                />
              </div>
            </div>
          </Box>
          <Box margin={'0 0 lg 0'}>
            <FormSelect
              id='preferContact'
              label={'How would you prefer us to contact you?'}
              {...register('preferContact')}
              error={errors?.preferContact}
              marginBottom
              defaultValue=''
            >
              <Options
                optionsData={[
                  { label: 'Email', value: 'email' },
                  { label: 'Call', value: 'phone_number' },
                ]}
              />
            </FormSelect>
          </Box>
          <Button type='submit' width='lg' disabled={!errors}>
            Submit
          </Button>
          <Box margin={'lg 0 lg 0'}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              size='invisible'
              badge='inline'
            />
          </Box>
        </form>
      </Flex>
    </>
  );
};
